import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router";
import { OptedOutBanner } from "@/features/predictions/components/OptedOutBanner";
import { OptedOutModal } from "@/features/predictions/components/OptedOutModal";
import { PredictionsHero } from "@/features/predictions/components/PredictionsHero";
import { PredictionsTabs } from "@/features/predictions/components/PredictionsTabs";
import { PronosticsOnboardingWizard } from "@/features/predictions/components/onboarding/PronosticsOnboardingWizard";
import { PREDICTIONS_SETTINGS_PATH } from "@/features/predictions/constants/paths";
import { useActiveCompetition } from "@/features/predictions/hooks/useActiveCompetition";
import { useMyPronosticsPreferences } from "@/features/predictions/hooks/useMyPronosticsPreferences";
import { usePronosticsOptedOutModal } from "@/features/predictions/hooks/usePronosticsOptedOutModal";

type Props = { children: ReactNode };

const MAIN_BG_CLASS = "bg-indigo-50";

const usePredictionsMainBackground = () => {
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    main.classList.add(MAIN_BG_CLASS);
    return () => {
      main.classList.remove(MAIN_BG_CLASS);
    };
  }, []);
};

export const PredictionsLayout = ({ children }: Props) => {
  usePredictionsMainBackground();
  const { data: preferences } = useMyPronosticsPreferences();
  const { data: competition } = useActiveCompetition();
  const location = useLocation();
  const optedOutModal = usePronosticsOptedOutModal();

  const isSettingsPage = location.pathname === PREDICTIONS_SETTINGS_PATH;
  const isOptedOut = preferences?.isParticipating === false;
  const showBanner = isOptedOut && !isSettingsPage && Boolean(competition);

  return (
    <div className="page-animation flex flex-col gap-6">
      <div className="flex flex-col gap-2 rounded-2xl bg-white p-2 shadow-sm dark:bg-gray-800">
        <PredictionsHero />
        <div className="px-3 py-2">
          <PredictionsTabs />
        </div>
      </div>
      {showBanner && competition && <OptedOutBanner competitionName={competition.name} />}
      {children}
      <PronosticsOnboardingWizard />
      {optedOutModal.isOpen && competition && (
        <OptedOutModal
          competitionName={competition.name}
          isJoining={optedOutModal.isJoining}
          onDismiss={optedOutModal.dismiss}
          onJoin={optedOutModal.join}
        />
      )}
    </div>
  );
};

PredictionsLayout.displayName = "PredictionsLayout";
